import React, { useState, useEffect, useRef } from "react";
import {
    Box,
    Typography,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TableContainer,
    Paper,
    TextField,
    IconButton,
    Tooltip,
    Button,
    Chip,
    MenuItem,
    Select,
    FormControl,
} from "@mui/material";
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    Refresh as RefreshIcon,
} from "@mui/icons-material";

const UNITS = ["MHz", "GHz", "kHz"];

/** Quarter-wave antenna length in inches: 2828 / frequencyMHz */
function calcAntennaLength(frequencyMHz) {
    const f = parseFloat(frequencyMHz);
    if (!f || f <= 0) return "";
    return `${Math.round(2828 / f)}"`;
}

function toMHz(value, unit) {
    const f = parseFloat(value);
    if (!f) return null;
    if (unit === "GHz") return f * 1000;
    if (unit === "kHz") return f / 1000;
    return f;
}

/** Default frequencies keyed by magnet type and GE field strength */
function getDefaultFrequencies(magnetType, magnetStrength) {
    let freqs = [];
    const magnet = (magnetType || "").toString();
    const strength = (magnetStrength || "").toString();
    const isGE = magnet.includes("GE");
    const geIsThree = magnet.includes("3.0") || strength.includes("3.0");
    const geIsOneFive = magnet.includes("1.5") || strength.includes("1.5");

    if (magnetType && magnetType.includes("High Spec")) {
        // High Spec has no default frequencies - user must enter all
        return [];
    } else if (isGE && geIsOneFive) {
        freqs = [
            { frequency: "51.251", unit: "MHz" },
            { frequency: "63.751", unit: "MHz" },
            { frequency: "76.751", unit: "MHz" },
        ];
    } else if (isGE && geIsThree) {
        freqs = [
            { frequency: "102.25", unit: "MHz" },
            { frequency: "127.751", unit: "MHz" },
            { frequency: "153.251", unit: "MHz" },
        ];
    } else if (isGE) {
        // Default GE set when a specific strength is not available
        freqs = [
            { frequency: "51.251", unit: "MHz" },
            { frequency: "63.751", unit: "MHz" },
            { frequency: "76.751", unit: "MHz" },
        ];
    } else {
        // All Others (Siemens, Philips, Fujifilm, Other)
        freqs = [
            { frequency: "64.251", unit: "MHz" },
            { frequency: "101.251", unit: "MHz" },
            { frequency: "128.251", unit: "MHz" },
        ];
    }
    return freqs.map((f) => ({
        ...f,
        reference: "",
        antennaLength: calcAntennaLength(f.frequency),
    }));
}

export default function FrequencyEntry({
    frequencies,
    magnetType,
    magnetStrength,
    onChange,
}) {
    const [rows, setRows] = useState(() =>
        frequencies && frequencies.length > 0
            ? frequencies
            : getDefaultFrequencies(magnetType, magnetStrength),
    );

    const prevMagnetKeyRef = useRef(
        `${magnetType || ""}|${magnetStrength || ""}`,
    );

    // Only auto-populate defaults if magnet type changes AND rows are currently empty
    useEffect(() => {
        const currentKey = `${magnetType || ""}|${magnetStrength || ""}`;
        if (currentKey !== prevMagnetKeyRef.current) {
            prevMagnetKeyRef.current = currentKey;
            if (!rows.length) {
                const defaults = getDefaultFrequencies(
                    magnetType,
                    magnetStrength,
                );
                setRows(defaults);
                onChange(defaults);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [magnetType, magnetStrength]);

    const notify = (updated) => {
        setRows(updated);
        onChange(updated);
    };

    const handleAdd = () => {
        const updated = [
            ...rows,
            { frequency: "", unit: "MHz", antennaLength: "", reference: "" },
        ];
        notify(updated);
    };

    const handleRemove = (idx) => {
        notify(rows.filter((_, i) => i !== idx));
    };

    const handleChange = (idx, field, value) => {
        const updated = rows.map((row, i) => {
            if (i !== idx) return row;
            const next = { ...row, [field]: value };
            // Auto-calc antenna length when frequency or unit changes
            if (field === "frequency" || field === "unit") {
                const mhz = toMHz(
                    field === "frequency" ? value : row.frequency,
                    field === "unit" ? value : row.unit,
                );
                next.antennaLength = mhz ? calcAntennaLength(mhz) : "";
            }
            return next;
        });
        notify(updated);
    };

    const handleLoadDefaults = () => {
        const defaults = getDefaultFrequencies(magnetType, magnetStrength);
        notify(defaults);
    };

    return (
        <Box>
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 1,
                    flexWrap: "wrap",
                    gap: 1,
                }}
            >
                <Typography variant="body2" color="text.secondary">
                    Enter frequencies and reference levels. Antenna length is
                    auto-calculated (¼ wave).
                </Typography>
                {magnetType && (
                    <Tooltip title="Load defaults for selected magnet">
                        <Button
                            size="small"
                            startIcon={<RefreshIcon />}
                            onClick={handleLoadDefaults}
                            variant="outlined"
                        >
                            Load Defaults
                        </Button>
                    </Tooltip>
                )}
            </Box>

            <TableContainer component={Paper} variant="outlined" sx={{ overflowX: "auto" }}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 700 }}>
                                Frequency
                            </TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Unit</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>
                                Antenna Length
                            </TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>
                                Reference (dBμV)
                            </TableCell>
                            <TableCell sx={{ width: 40 }} />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={5}>
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{ py: 1 }}
                                    >
                                        No frequencies added. Click "+ Add" or
                                        Load Defaults.
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        )}
                        {rows.map((row, idx) => (
                            <TableRow key={idx}>
                                <TableCell>
                                    <TextField
                                        size="small"
                                        value={row.frequency}
                                        onChange={(e) =>
                                            handleChange(
                                                idx,
                                                "frequency",
                                                e.target.value,
                                            )
                                        }
                                        inputProps={{ style: { width: 90 } }}
                                        placeholder="64.000"
                                    />
                                </TableCell>
                                <TableCell>
                                    <FormControl
                                        size="small"
                                        sx={{ minWidth: 80 }}
                                    >
                                        <Select
                                            value={row.unit || "MHz"}
                                            onChange={(e) =>
                                                handleChange(
                                                    idx,
                                                    "unit",
                                                    e.target.value,
                                                )
                                            }
                                        >
                                            {UNITS.map((u) => (
                                                <MenuItem key={u} value={u}>
                                                    {u}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </TableCell>
                                <TableCell>
                                    <Chip
                                        label={row.antennaLength || "—"}
                                        size="small"
                                        variant="outlined"
                                    />
                                </TableCell>
                                <TableCell>
                                    <TextField
                                        size="small"
                                        value={row.reference}
                                        onChange={(e) =>
                                            handleChange(
                                                idx,
                                                "reference",
                                                e.target.value,
                                            )
                                        }
                                        inputProps={{ style: { width: 70 } }}
                                        placeholder="110"
                                    />
                                </TableCell>
                                <TableCell>
                                    <Tooltip title="Remove">
                                        <IconButton
                                            size="small"
                                            color="error"
                                            onClick={() => handleRemove(idx)}
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <Button
                startIcon={<AddIcon />}
                onClick={handleAdd}
                size="small"
                sx={{ mt: 1 }}
                variant="outlined"
            >
                Add Frequency
            </Button>
        </Box>
    );
}
